import { pcbLayoutNestedNodeDelimiter, pcbLayoutRootNodeId, PcbNodeTypes, } from "@buildwithflux/models";
import { mapSubmoduleNetsToHostNets } from "../../../document/functions/submodules";
import { getAncestorUidByType, getNodesOfType } from "../../../nodes/traversal";
export function computeEffectiveNetForNode(node, treeNodes, nets, cache) {
    // Pads are not children of pcb net objects, but they are part of nets indirectly anyway
    // This relationship is maintained by the terminals map in the net, which contains the uids of the member pads
    if (node.type === PcbNodeTypes.pad) {
        const attemptSharedNetUid = getNetUidForTerminal(node.uid, treeNodes, cache);
        if (attemptSharedNetUid) {
            return attemptSharedNetUid;
        }
        else {
            // It's possible that the normal approach using the `terminals` or `submoduleTerminals` list on the host net doesn't work, for example
            // if the pad is in a submodule net that is not connected to host copper. In that case the pad uid will be prefixed with the submodule uid, but
            // the terminal list of the submodule net will not contain that prefix (internal submodule nets).
            // To correctly match the submodule pad to the submodule net, we need to strip the submodule uid prefix.
            const possibleSubLayoutUid = getAncestorUidByType(treeNodes, node.uid, PcbNodeTypes.layout);
            const possibleSubLayout = possibleSubLayoutUid ? treeNodes[possibleSubLayoutUid] : undefined;
            const isInSubLayout = possibleSubLayout?.parentUid !== pcbLayoutRootNodeId;
            const normalisedUid = !isInSubLayout
                ? node.uid
                : node.uid.split(pcbLayoutNestedNodeDelimiter).slice(1).join(pcbLayoutNestedNodeDelimiter);
            return getNetUidForTerminal(normalisedUid, treeNodes, cache);
        }
    }
    // Those other nodes are children of pcb net objects instead, but there are some special cases
    if (node.type === PcbNodeTypes.via ||
        node.type === PcbNodeTypes.routeSegment ||
        node.type === PcbNodeTypes.smartVia ||
        node.type === PcbNodeTypes.fill ||
        node.type === PcbNodeTypes.polygon) {
        const ownNetId = getNearestNetAncestorUid(node.uid, treeNodes);
        // Those nodes should always be under a net, if they are not then they are not part of any anyway
        if (!ownNetId)
            return undefined;
        const submoduleNetsToHostNets = getSubmoduleNetToHostNetMapping(cache, treeNodes);
        if (nets && ownNetId in nets) {
            // If the node parent net is in the host nets, that node cannot be from a submodule, so the effective net is just the parent net uid
            return ownNetId;
        }
        else if (ownNetId in submoduleNetsToHostNets) {
            // If a submodule-host net mapping exists for this net, return the host net uid
            return submoduleNetsToHostNets[ownNetId];
        }
        else {
            // It's also possible that the parent net is from a submodule, but it doesn't have a host net mapping (internal submodule net). This occurs when the submodule net is not connected to anything in the host project.
            // In that case, we return the parent net uid, which is the effective net for this node
            return ownNetId;
        }
    }
    // Nets themselves can be root level (host nets) or submodule nets
    // In case of a submodule net, it can be merged with a host net (but not always, for example in case of "internal submodule" nets that are not connected to anything in the host)
    // We want to pass this info down since it is used for copper fills
    if (node.type === PcbNodeTypes.net) {
        const submoduleNetsToHostNets = getSubmoduleNetToHostNetMapping(cache, treeNodes);
        // If the net is a submodule net, return its host net uid
        if (node.uid in submoduleNetsToHostNets) {
            return submoduleNetsToHostNets[node.uid];
        }
        // Otherwise, return its own uid
        return node.uid;
    }
    return undefined;
}
function getNearestNetAncestorUid(nodeUid, treeNodes) {
    return getAncestorUidByType(treeNodes, nodeUid, PcbNodeTypes.net);
}
function getNetUidForTerminal(terminalUid, treeNodes, cache) {
    const allNetNodes = getNodesOfType(treeNodes, PcbNodeTypes.net, cache.allNetNodesCache);
    function netNodeHasTerminal(netNode, terminalUid) {
        return terminalUid in (netNode.terminals ?? {}) || terminalUid in (netNode.submoduleTerminals ?? {});
    }
    const netNode = allNetNodes.find((n) => netNodeHasTerminal(n, terminalUid));
    return netNode?.uid;
}
/** For each Net node within a submodule, get the host net uid it's associated with.
 * See full explanation in `mapSubmoduleNetsToHostNets`.
 */
function getSubmoduleNetToHostNetMapping(cache, treeNodes) {
    if (!cache.submoduleNetsToHostNetsCache) {
        // Cache it so we only need to do this iteration on all nodes once per instantiation
        cache.submoduleNetsToHostNetsCache = mapSubmoduleNetsToHostNets(treeNodes, cache.allNetNodesCache);
    }
    return cache.submoduleNetsToHostNetsCache;
}
//# sourceMappingURL=computeEffectiveNetForNode.js.map